import { of, throwError } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { forkJoin } from 'rxjs'
import DataHelper from 'framework/helpers/data'
import { serialize } from 'helpers/index.js'
import VoltError from 'VoltError'
import Constants from 'api-constants'
import { PlaybackAttributes } from 'models'
import { getKidFromManifest } from './manifestParser'
import Fetch from '../fetch'
import GenericFetch from 'framework/genericfetch'
import { EVENT } from 'services/adobe/playerAnalytics/types'
import { adobeAnalyticsFactory } from '../factories'

/**
 * Deals with Playback requirements (session creation, tracking etc)
 */
export default class PlayerApi extends Fetch {
    static TAG = 'PlayerApi'
    static PLAYBACK_TAG = '[_PLAYBACK_]'

    constructor(config, otherApis) {
        super(config, otherApis)
        // Cache for managing KID caching
        this._kidCache = {}
        this.playerAnalyticsApi = otherApis?.playerAnalyticsApi
    }

    /**
     * Get the url a associated Live manifest
     *
     * @param {Object} args
     * @param {String} args.platformChannelId
     * @param {String} args.playbackUrl
     * @param {String} args.drmProvider
     * @param {Object} args.channelAnalytics
     * @return {Observable<SessionData>}
     */
    getLiveStreamUrl({ platformChannelId, playbackUrl, drmProvider, channelAnalytics } = {}) {
        const { platform } = this.config
        if (!this._isDeviceSupported(platform)) {
            return throwError(
                new VoltError(VoltError.codes.PLAYER_DEVICE_NOT_SUPPORTED, {
                    extraLog:
                        'Device not supported by mediacorp. Internal consistency not a backend error.',
                })
            )
        }

        const { forceManifest } = this._getMediaCorpConfig()
        if (platformChannelId && forceManifest && forceManifest[platformChannelId]) {
            playbackUrl = forceManifest[platformChannelId]
        }

        this.logger.info(PlayerApi.PLAYBACK_TAG, 'Getting Mediacorp Stream session...')
        return (this._isDrmTokenValid() ? of(null) : this._retrieveDrmToken()).pipe(
            mergeMap(() => {
                return forkJoin([
                    this._extractKidFromManifest(playbackUrl),
                    this._retrieveTrackingSession(),
                ]).pipe(
                    mergeMap(([kid, trackingSession]) => {
                        return this._initDrm(kid).pipe(
                            mergeMap(() => {
                                return this.playerAnalyticsApi.createSession({
                                    channelAnalytics: adobeAnalyticsFactory(channelAnalytics),
                                    customMetadata: channelAnalytics,
                                    playbackUrl,
                                    trackingSessionId: trackingSession.sessionId,
                                    deviceTypeId: this._getDeviceType(platform),
                                })
                            }),
                            mergeMap(() => {
                                const { sessionId, network, trackingId, trackingSegment } =
                                    trackingSession || {}
                                const { drmBaseUrl, drmType } = this._getLicenseBaseUrl(platform)

                                return of(
                                    new PlaybackAttributes({
                                        url: playbackUrl,
                                        assetId: platformChannelId,
                                        keepAlive: this.playerAnalyticsApi.getKeepAlivePeriod(), // No Keep alive for this backend
                                        drm: {
                                            licenseUrl: `${drmBaseUrl}${serialize({
                                                Token: DataHelper.getInstance().getData(
                                                    DataHelper.STORE_KEY.EXTERNAL_DRM_ACCESS_TOKEN
                                                ),
                                                MeID: trackingId,
                                                SegmentID: trackingSegment,
                                                // TODO: Rollback and keep the issue to avoid error while Mediacorp is fixing their issue
                                                DeviceID: this._getDeviceUUID(),
                                                KID: kid,
                                            })}`,
                                        },
                                        drmType,
                                        drmProvider,
                                        sessionId,
                                        network,
                                        trackingId,
                                        trackingSegment,
                                    })
                                )
                            })
                        )
                    }),
                    catchError((error) => {
                        // Invalid KID Caching before throwing an error
                        this._cacheKid(playbackUrl, undefined)

                        return throwError(error)
                    })
                )
            })
        )
    }

    /**
     * Retrieve a DRM Access Token method
     */
    _retrieveDrmToken() {
        const { partnerId } = this._getMediaCorpConfig()
        if (!partnerId) {
            return throwError(
                new VoltError(VoltError.codes.PLAYER_SESSION_ERROR_DUE_TO_INVALID_PARAMETER, {
                    extraLog: '[IN APP ERROR] Missing MC configurations attributes',
                })
            )
        }

        return this.fetch({
            url: `${this.config.urls.externalDrmUrl}/GetToken`,
            method: 'POST',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
            },
            body: {
                partnerId: partnerId,
            },
            log: 'GET TOKEN',
        }).pipe(
            mergeMap(({ response = {} }) => {
                return DataHelper.getInstance().storeData([
                    [DataHelper.STORE_KEY.EXTERNAL_DRM_ACCESS_TOKEN, response.token],
                    [
                        DataHelper.STORE_KEY.EXTERNAL_DRM_ACCESS_TOKEN_EXPIRATION_TIME_MS,
                        response.expiry,
                    ],
                    [DataHelper.STORE_KEY.EXTERNAL_DRM_ACCESS_TOKEN_ISSUED_TIME_MS, Date.now()],
                ])
            })
        )
    }

    /**
     * Retrieve tracking parameters from Tracking server
     * @returns {Object} { sessionId, network, trackingId, trackingSegment }
     */
    _retrieveTrackingSession() {
        const { platform } = this.config
        const deviceUUID = this._getDeviceUUID()
        const { network, partnerToken } = this._getMediaCorpConfig()
        if (!network || !partnerToken) {
            return throwError(
                new VoltError(VoltError.codes.PLAYER_SESSION_ERROR_DUE_TO_INVALID_PARAMETER, {
                    extraLog: '[IN APP ERROR] Missing MC configurations attributes',
                })
            )
        }
        if (!deviceUUID) {
            return throwError(
                new VoltError(VoltError.codes.PLAYER_DEVICE_INVALID, {
                    extraLog: '[Internal Error] Missing device UUID before contacting the backend',
                })
            )
        }

        this.logger.info(PlayerApi.PLAYBACK_TAG, 'Getting Mediacorp MeID...')
        const url = `${this.config.urls.externalDrmTrackerUrl}/api/mobile_app/init${serialize({
            network: network,
            token: partnerToken,
            device_id: deviceUUID,
            device_id_type: this._getDeviceType(platform),
            meid: this._lastMeId, // According to the documentation: MeID from previous step response data.
        })}`
        return this.fetch({
            url,
            method: 'GET',
            log: 'GET MEID',
        }).pipe(
            mergeMap(({ response = {} }) => {
                this.logger.info(PlayerApi.PLAYBACK_TAG, 'Mediacorp MeID tracking received...')
                this._lastMeId = response.meid
                return of({
                    sessionId: response.session_id,
                    network: response.network,
                    trackingId: response.meid,
                    trackingSegment: (response.meid_seg || []).join(','),
                })
            })
        )
    }

    /**
     * Initiate the DRM Server
     * New flow required by MediaCorp VOLTSTB-3389
     * @returns {Object} { sessionId, network, trackingId, trackingSegment }
     */
    _initDrm(kid) {
        const { enableInitDrm = false } = this._getMediaCorpConfig()
        if (!enableInitDrm) {
            return of(true)
        }

        const deviceUUID = this._getDeviceUUID()
        if (!deviceUUID) {
            return throwError(
                new VoltError(VoltError.codes.PLAYER_DEVICE_INVALID, {
                    extraLog: '[Internal Error] Missing device UUID before contacting the backend',
                })
            )
        }

        this.logger.info(PlayerApi.PLAYBACK_TAG, 'Pre-Initialization of the Mediacorp DRM...')
        const url = `${this.config.urls.externalDrmUrl}/Initialize${serialize({
            KID: kid,
            DeviceID: deviceUUID,
            Token: DataHelper.getInstance().getData(DataHelper.STORE_KEY.EXTERNAL_DRM_ACCESS_TOKEN),
        })}`
        return this.fetch({
            url,
            method: 'POST',
            log: 'INIT DRM',
        }).pipe(
            catchError((error) => {
                this.logger.error('Cannot initialize the DRM due to the error', error)
                return throwError(
                    new VoltError(VoltError.codes.PLAYER_DRM_DATA_INIT_FAILURE, {
                        extraLog: 'Cannot initialize the mediacorp DRM Server',
                    })
                )
            })
        )
    }

    /**
     * Extract KID by parsing the manifest file
     * @param {String} playbackUrl Playback URL
     * @returns {Observable<boolean>}
     */
    _extractKidFromManifest(playbackUrl) {
        if (!playbackUrl) {
            return throwError(
                new VoltError(VoltError.codes.PLAYER_ENTITLEMENT_NO_SUCH_ASSET, {
                    extraLog: '[IN APP ERROR] Empty Manifest URL',
                })
            )
        }

        const kidFromCache = this._getKidFromCache(playbackUrl)
        if (kidFromCache) {
            this.logger.info(
                PlayerApi.PLAYBACK_TAG,
                '[Caching] Kid found for this channel, NO backend query'
            )
            return of(kidFromCache)
        }

        this.logger.info(
            PlayerApi.PLAYBACK_TAG,
            '[Caching] No Kid found for this channel, initiates a backend query'
        )

        const { enableCorsMpdParser = true } = this._getMediaCorpConfig()
        return this.fetch({
            url: playbackUrl,
            method: 'GET',
            log: 'mpdParser',
            headers: { 'Content-Type': 'application/dash+xml' },
            followRedirect: true,
            crossDomain: enableCorsMpdParser,
            responseType: GenericFetch.responseType.text,
        }).pipe(
            mergeMap(({ response }) => {
                this.logger.info(PlayerApi.PLAYBACK_TAG, 'Manifest received for parsing the KID')
                const kid = getKidFromManifest(response)
                if (!kid) {
                    return throwError(
                        new VoltError(VoltError.codes.PLAYER_DRM_CANNOT_EXTRACT_KID, {
                            extraLog: '[IN APP ERROR] Fail to parse manifest',
                        })
                    )
                }
                this.logger.info(PlayerApi.PLAYBACK_TAG, 'KID parsed')

                this._cacheKid(playbackUrl, kid)

                return of(kid)
            }),
            catchError((error) => {
                this.logger.error('Cannot parse the manifest', error)
                return throwError(error)
            })
        )
    }

    /**
     * Check internally if the access token is still valid or not
     * @returns {Boolean}
     */
    _isDrmTokenValid = () => {
        const drmAccessToken = DataHelper.getInstance().getData(
            DataHelper.STORE_KEY.EXTERNAL_DRM_ACCESS_TOKEN
        )
        const drmTokenExpirationTimeMsecs = DataHelper.getInstance().getData(
            DataHelper.STORE_KEY.EXTERNAL_DRM_ACCESS_TOKEN_EXPIRATION_TIME_MS
        )
        const drmTokenIssuedTimeMsecs = DataHelper.getInstance().getData(
            DataHelper.STORE_KEY.EXTERNAL_DRM_ACCESS_TOKEN_ISSUED_TIME_MS
        )

        const isDrmTokenValid =
            drmAccessToken &&
            !!drmTokenExpirationTimeMsecs &&
            parseInt(drmTokenExpirationTimeMsecs) > Date.now()

        if (isDrmTokenValid) {
            const { refreshTokenTimeoutMs } = this._getMediaCorpConfig()
            const forceRefresh = !(drmTokenIssuedTimeMsecs + refreshTokenTimeoutMs > Date.now())
            if (forceRefresh) {
                this.logger.info(
                    `Force access token refresh due to configuration timeout ${refreshTokenTimeoutMs}`
                )
                return false
            }
        }

        return isDrmTokenValid
    }

    notifyStartSessionEvent({ sessionId, timeOffset }) {
        return this.playerAnalyticsApi.reportSessionEvent(EVENT.PLAY, timeOffset)
    }

    notifyEndSessionEvent({
        sessionId,
        timeOffset,
        reason = Constants.session.endReasons.unknown,
    }) {
        return this.playerAnalyticsApi.reportSessionEvent(EVENT.SESSION_END, timeOffset)
    }

    notifyPauseSessionEvent({ sessionId, timeOffset }) {
        return this.playerAnalyticsApi.reportSessionEvent(EVENT.PAUSE, timeOffset)
    }

    notifySeekSessionEvent({ sessionId, timeOffset, previousTimeOffset }) {
        return this.playerAnalyticsApi.reportSessionEvent('TODO PROVIDE THE EXPECTED PARAMETER')
    }

    keepAlive({ sessionId, timeOffset }) {
        return this.playerAnalyticsApi.reportSessionEvent(EVENT.PING, timeOffset)
    }

    /// --------------------------- NOT SUPPORTED -----------------------------
    getDvrStreamUrl({ recordingId: id }) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getDvrStreamUrl] Feature not supported',
            })
        )
    }

    getVodStreamUrl({
        playbackContentId,
        titleId,
        screenDimensions,
        isTrailer,
        subscriptionIds,
        streams,
        gpsPosition,
        initialOffset,
    }) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getVodStreamUrl] Feature not supported',
            })
        )
    }

    getStartOverStreamUrl({ channelId, programId }) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getStartOverStreamUrl] Feature not supported',
            })
        )
    }

    /// --------------------------- TOOLBOX -----------------------------
    /**
     * Get Device type needed to be used (SDK not supported by web app)
     * @param {String} platform
     * @returns {String} Device Type
     */
    _getDeviceType(platform) {
        switch (platform) {
            case Constants.platform.ios:
            case Constants.platform.tvos:
                return 'IDFV'
            case Constants.platform.android:
            case Constants.platform.androidTvStb:
            case Constants.platform.androidTv:
            case Constants.platform.amazonTv:
                return 'GAID'
            default:
                return ''
        }
    }

    _parseUrl(url) {
        // IMPORTANT : Backend fall in error if we provide a double slash
        return url && url.replace(/([^:]\/)\/+/g, '$1')
    }
    /**
     * Get License Base URL
     * @param {String} platform
     * @returns {Object} { drmBaseUrl, drmType }
     */
    _getLicenseBaseUrl(platform) {
        let drmBaseUrl
        let drmType

        switch (platform) {
            case Constants.platform.ios:
            case Constants.platform.tvos:
            case Constants.platform.safari:
                drmBaseUrl = this._parseUrl(`${this.config.urls.externalDrmUrl}/AcquireFairplay`)
                drmType = PlaybackAttributes.DRM_TYPE.FAIRPLAY
                break

            case Constants.platform.android:
            case Constants.platform.androidTvStb:
            case Constants.platform.androidTv:
            case Constants.platform.amazonTv:
            case Constants.platform.smartTvOs:
            case Constants.platform.chrome:
            case Constants.platform.firefox:
            case Constants.platform.edgeChromium:
            case Constants.platform.chromecast:
                drmBaseUrl = this._parseUrl(`${this.config.urls.externalDrmUrl}/AcquireWidevine`)
                drmType = PlaybackAttributes.DRM_TYPE.WIDEVINE
                break
            default:
                break
        }

        return { drmBaseUrl, drmType }
    }

    /**
     * Returns if the device is supported or not (FOR WEB needs to use another way to pull the MeID)
     * @param {String} platform
     * @returns {Boolean} Returns if the device is supported or not
     */
    _isDeviceSupported(platform) {
        switch (platform) {
            case Constants.platform.ios:
            case Constants.platform.tvos:
            case Constants.platform.android:
            case Constants.platform.androidTvStb:
            case Constants.platform.androidTv:
            case Constants.platform.smartTvOs:
            case Constants.platform.amazonTv:
                return true
            default:
                return false
        }
    }

    /**
     * Returns the configurations for mediacorp
     * @returns {Object} return the config, fallback to empty object if missing
     */
    _getMediaCorpConfig() {
        return this.config.mediacorp || {}
    }

    /**
     * Cache KID
     * @param {String} playbackUrl Playback URL
     * @param {String} kid
     */
    _cacheKid(playbackUrl, kid) {
        if (!playbackUrl || !kid) return

        this.logger.info(
            kid
                ? `'[Caching] Cache KID for ${this._getMediaCorpConfig().kidCachingIntervalMs} ms`
                : '[CACHING] Invalid Cache'
        )

        this._kidCache[playbackUrl] = {
            time: Date.now(),
            kid,
        }
    }

    /**
     * Get KID from cache
     * @param {String} playbackUrl Playback URL
     * @returns {String|undefined} KID or undefined if cache if invalid
     */
    _getKidFromCache(playbackUrl) {
        const cache = this._kidCache[playbackUrl] || {}
        const { kidCachingIntervalMs } = this._getMediaCorpConfig()
        if (kidCachingIntervalMs && cache.time && cache.time + kidCachingIntervalMs > Date.now()) {
            return cache.kid
        }

        return undefined
    }

    /**
     * Get KID from cache
     * @param {String} playbackUrl Playback URL
     * @returns {String|undefined} KID or undefined if cache if invalid
     */
    _getDeviceUUID() {
        const deviceUUID = this.config.uniqueDeviceId // If needed to be another parameter, provide it through props
        return deviceUUID
    }

    /**
     * Error extraction. Inherited from fetch
     * @param {Object} response
     * @returns
     */
    extractError(response) {
        if (!response) {
            return { error: 'unknown', description: 'unknown' }
        }
        if (response.error) {
            return {
                error: response.error,
                description: response.error_description,
            }
        }

        return { error: 'unknown', description: 'unknown' }
    }
}
